<template>
  <header class="app-header">
    <div class="app-header__row">
      <RouterLink class="app-header__brand" :to="isAuthenticated ? '/dashboard' : '/'">
        <GonkaGameLogo />
      </RouterLink>

      <div class="app-header__actions">
        <template v-if="isAuthenticated">
          <span v-if="userLabel" class="app-header__email">{{ userLabel }}</span>
          <!-- Колокольчик живёт внутри той же ветки «свой», что и почта: он
               берётся из кеша профиля вместе со всей шапкой и потому не
               возвращает мигание, а гостю не показывается вовсе. -->
          <NotificationsBell />
        </template>
        <template v-else>
          <button class="app-header__btn app-header__btn--primary" type="button" @click="showAuth = true">
            {{ $t('auth.login') }}
          </button>
        </template>
      </div>
    </div>

    <!-- IGRA-20 fix: single unified email+OTP form, replaces the former
         separate login modal — no more "Войти" vs "Зарегистрироваться" split. -->
    <RegistrationModal v-model="showAuth" />
  </header>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'

import GonkaGameLogo from '@/components/common/GonkaGameLogo.vue'
import RegistrationModal from '@/modules/auth/components/RegistrationModal.vue'
import NotificationsBell from '@/modules/notifications/components/NotificationsBell.vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'

const profileStore = useProfileStore()
// IGRA — без мигания: пока летит `base-info`, шапку рисует кеш прошлого визита
// (см. modules/profile/services/profileCache.ts). Раньше здесь была прямая
// проверка `!!profile`, и человек успевал увидеть кнопку «Войти».
// Имя, а не почта: у аккаунта из Telegram адрес технический (см. displayName).
const { isAuthenticated, displayName: userLabel } = storeToRefs(profileStore)

const showAuth = ref(false)
</script>

<style scoped lang="scss">
// IGRA-20 shell header: logo + authenticated user email (or guest CTAs).
// Mobile-first — base width 560px, matches the key.igra.click cabinet.
//
// Шапка закреплена (fixed, не sticky) — решение оператора 2026-09-23: на
// iPhone sticky-шапка ехала вместе с прокруткой и открывала «дырку». Высота
// одна на все состояния (гость / свой) — `--app-header-height`, на неё же
// AppLayout отступает сверху, чтобы контент не уходил под шапку.
.app-header {
  display: flex;
  align-items: center;
  background: var(--ds-surface);
  border-bottom: 1px solid var(--ds-border);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  box-sizing: border-box; // рамка входит в --app-header-height
  height: var(--app-header-height);

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 560px;
    height: 100%;
    margin: 0 auto;
    padding: 0 16px;
    gap: 12px;
  }

  &__brand {
    display: flex;
    align-items: center;
    text-decoration: none;
    flex: none;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
    color: var(--ds-text-secondary);
    font-size: var(--ds-text-size-body);
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    border-radius: var(--ds-radius-control);
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-semibold);
    text-decoration: none;
    cursor: pointer;
    border: none;
    white-space: nowrap;
  }

  &__btn--text {
    background: transparent;
    color: var(--ds-text-secondary);

    &:hover {
      color: var(--ds-text);
    }
  }

  &__btn--primary {
    background: linear-gradient(180deg, var(--ds-accent-light), var(--ds-accent));
    color: var(--ds-text-on-accent);
    box-shadow: var(--ds-glow-cta);
  }
}
</style>
